import React, { useState } from "react";
import styled from "styled-components";
import { FaTimes, FaCloudUploadAlt, FaClipboardCheck, FaExclamationCircle } from "react-icons/fa";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase/firebaseConfig";

const guinda = "#611232";
const guindaLight = "#8b1d47";

export default function ModalProceso({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    correo: "",
  });
  const [archivo, setArchivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setFormData({ nombre: "", telefono: "", correo: "" });
    setArchivo(null);
    setError("");
    setSuccess(false);
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Límite de 2.5 MB
    const MAX_SIZE = 2.5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setError("El archivo es demasiado pesado (Máximo 2.5 MB).");
      setArchivo(null);
      e.target.value = ""; // Limpiar el input
    } else {
      setError("");
      setArchivo(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let archivoUrl = "";
      if (archivo) {
        const storageRef = ref(storage, `evidencias/${Date.now()}_${archivo.name}`);
        const snapshot = await uploadBytes(storageRef, archivo);
        archivoUrl = await getDownloadURL(snapshot.ref);
      }

      await addDoc(collection(db, "solicitudes"), {
        nombre: formData.nombre,
        telefono: formData.telefono,
        correo: formData.correo,
        archivoUrl,
        fecha: new Date().toLocaleDateString("es-MX", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        createdAt: serverTimestamp(),
        estado: "Nueva",
      });

      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(`Ocurrió un error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <FaTimes />
        </CloseButton>

        {success ? (
          <SuccessContainer>
            <IconoExito>
              <FaClipboardCheck />
            </IconoExito>
            <Titulo>¡Información Enviada!</Titulo>
            <TextoInfo>
              El Licenciado asignado realizará una breve revisión y se pondrá en contacto con usted a la brevedad.
            </TextoInfo>
            <BotonSubmit onClick={handleClose}>Aceptar</BotonSubmit>
          </SuccessContainer>
        ) : (
          <>
            <IconContainer>
              <IconWrapper>
                <FaClipboardCheck />
              </IconWrapper>
            </IconContainer>
            <Titulo>Comenzar mi proceso</Titulo>

            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <Input
                  type="text"
                  placeholder="Nombre completo"
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                />
              </InputGroup>

              <InputGroup>
                <Input
                  type="tel"
                  placeholder="Numero de telefono"
                  required
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                />
              </InputGroup>

              <InputGroup>
                <Input
                  type="email"
                  placeholder="Correo electrónico"
                  required
                  value={formData.correo}
                  onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                />
              </InputGroup>

              <DropZone>
                <input
                  type="file"
                  id="archivo-upload"
                  onChange={handleFileChange}
                  accept=".pdf,image/*"
                  style={{ display: "none" }}
                />
                <label htmlFor="archivo-upload" style={{ cursor: "pointer", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <FaCloudUploadAlt size={60} color="#b3b3b3" />
                  <TextoUpload>
                    {archivo ? archivo.name : "Subir archivos desde la galería o archivos PDF (Máx 2.5MB)"}
                  </TextoUpload>
                </label>
              </DropZone>

              <TextoAviso>
                Una vez anexada la información el Licenciado asignado realizara una breve revisión y se pondrá en contacto con usted
              </TextoAviso>

              {error && (
                <ErrorBox>
                  <FaExclamationCircle /> {error}
                </ErrorBox>
              )}

              <BotonSubmit type="submit" disabled={loading}>
                {loading ? "Subiendo..." : "Subir información"}
              </BotonSubmit>
            </Form>
          </>
        )}
      </ModalContent>
    </Overlay>
  );
}

// Estilos
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 1.5rem;
`;

const ModalContent = styled.div`
  background: white;
  width: 100%;
  max-width: 450px;
  padding: 3rem 2.5rem;
  border-radius: 2rem;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #ccc;
  cursor: pointer;
  transition: color 0.2s;
  &:hover {
    color: #333;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
`;

const IconWrapper = styled.div`
  width: 65px;
  height: 65px;
  background: ${guinda};
  color: white;
  border-radius: 20px;
  display: grid;
  place-items: center;
  font-size: 1.8rem;
`;

const Titulo = styled.h2`
  margin: 0 0 2rem;
  font-weight: 900;
  color: #1a1a1a;
  font-size: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const InputGroup = styled.div`
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1.2rem;
  border: 1.5px solid #e0e0e0;
  border-radius: 100px;
  font-size: 0.95rem;
  font-family: inherit;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${guinda};
    box-shadow: 0 0 0 4px rgba(97, 18, 50, 0.1);
  }
  &::placeholder {
    color: #999;
  }
`;

const DropZone = styled.div`
  border: 2px solid #e0e0e0;
  border-radius: 20px;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: all 0.2s;
  margin-top: 0.5rem;

  &:hover {
    border-color: ${guindaLight};
    background: #fafafa;
  }
`;

const TextoUpload = styled.span`
  margin-top: 1rem;
  color: ${guinda};
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.4;
`;

const TextoAviso = styled.p`
  color: #888;
  font-size: 0.8rem;
  text-align: center;
  line-height: 1.5;
  margin: 1rem 0;
`;

const ErrorBox = styled.div`
  color: #d93025;
  background: #fdf2f2;
  padding: 0.8rem;
  border-radius: 10px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
`;

const BotonSubmit = styled.button`
  background: ${guinda};
  color: white;
  border: none;
  border-radius: 100px;
  padding: 1.2rem;
  font-size: 1.1rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  box-shadow: 0 10px 20px rgba(97, 18, 50, 0.15);

  &:hover {
    background: #4a0d26;
    transform: translateY(-2px);
    box-shadow: 0 15px 30px rgba(97, 18, 50, 0.25);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 0;
`;

const IconoExito = styled.div`
  width: 80px;
  height: 80px;
  background: #e6f4ea;
  color: #137333;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
`;

const TextoInfo = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 2rem;
`;
